import { useTranslate } from "@similarweb/i18n-provider";
import type { ActivationAlertsContractShoppingAdFeedGroup } from "@similarweb/pro-api-client";
import { useTracking } from "@similarweb/tracking-provider";
import { Badge, Carousel } from "@similarweb/ui-components";

import { AlertsGroupCarousel } from "../../common/AlertsGroupCarousel";
import { CardLayout } from "../../common/card-layout/CardLayout";
import { ChannelIcon } from "../../common/ChannelIcon";
import { SeeAllButton } from "../../common/SeeAllButton";
import { SeeMoreCard } from "../../common/SeeMoreCard";
import { navigateToSearchAds } from "../utils/navigateToAdsIntel";
import { adsTypeTranslationKeyMap } from "../utils/translationKeysConfig";

import { shoppingAdCardSize, ShoppingAdCard } from "./ShoppingAdCard";

interface IShoppingAdsAlertProps {
    country?: number;
    favicon: string;
    group: ActivationAlertsContractShoppingAdFeedGroup;
}

export const ShoppingAdsAlert = ({ country, favicon, group }: IShoppingAdsAlertProps) => {
    const { translate } = useTranslate();
    const { track } = useTracking();

    const hasMoreAds = group.totalCount > group.ads.length;

    const handleAdsPageNavigation = () => {
        track("Alert Card", "click", "open alert/alerts/shopping-ads");
        navigateToSearchAds({ domain: group.domain, country, selectedPageTab: "Product" });
    };

    return (
        <CardLayout
            iconUrl={favicon}
            onClick={handleAdsPageNavigation}
            data-automation="shopping-ads-alert"
            data-domain={group.domain}
        >
            <CardLayout.Header>
                <Badge data-automation="alert-badge">{translate(adsTypeTranslationKeyMap.Shopping)}</Badge>
                <CardLayout.Title>
                    {translate("activation.alerts.cards.search-ads.title", {
                        count: group.totalCount,
                        domain: group.domain,
                    })}
                </CardLayout.Title>
                <ChannelIcon channel="Google" />
            </CardLayout.Header>
            <CardLayout.Content>
                <AlertsGroupCarousel>
                    {group.ads.map((ad) => (
                        <Carousel.Item key={ad.id}>
                            <ShoppingAdCard
                                price={ad.price}
                                title={ad.title ?? ""}
                                imageUrl={ad.imageUrl}
                                firstSeen={ad.firstSeen}
                                landingPage={ad.landingPage ?? ""}
                            />
                        </Carousel.Item>
                    ))}
                    {hasMoreAds ? (
                        <Carousel.Item key="see-more-card">
                            <SeeMoreCard
                                width={shoppingAdCardSize.width}
                                height={shoppingAdCardSize.height}
                                onClick={handleAdsPageNavigation}
                                dataAutomation="shopping-ad-see-more-card"
                            />
                        </Carousel.Item>
                    ) : null}
                </AlertsGroupCarousel>
                <SeeAllButton
                    onClick={handleAdsPageNavigation}
                    isDisabled={!group.ads.length}
                    dataAutomation="see-all-ads-button"
                >
                    {translate("activation.alerts.cards.ads.see-all")}
                </SeeAllButton>
            </CardLayout.Content>
        </CardLayout>
    );
};
